import isNumber from 'lodash/isNumber.js';
import clamp from 'lodash/clamp.js';
import { ResizeObserver } from 'vue-resize';
import { getRelativeStrokeWidth, getRelativeThickness, isInProgress, isComplete, isNotStarted, queue, getTrickleProgress } from './utils.js';
import { isPercent } from '../../utils/dom.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoProgressBar',
  components: {
    ResizeObserver
  },
  props: {
    value: {
      type: Number,
      default: 0,
      required: true,
      validator: val => val >= 0 && val <= 100
    },
    height: {
      type: Number,
      default: 8
    },
    width: {
      type: [Number, String],
      default: 200
    },
    strokeWidth: {
      type: Number,
      default: 1
    },
    colors: {
      type: Object,
      default: () => ({
        default: {
          fill: '#DADCE5',
          stroke: '#DADCE5'
        },
        progress: {
          fill: '#128297',
          stroke: '#128297'
        },
        complete: {
          fill: '#00C583',
          stroke: '#00C583'
        }
      })
    },
    trickle: {
      type: Boolean,
      default: false
    },
    trickleSpeed: {
      type: Number,
      default: 200
    },
    easing: {
      type: String,
      default: 'ease-in-out'
    },
    speed: {
      type: Number,
      default: 200
    },
    radius: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      computedWidth: parseInt(this.width, 10),
      internalValue: this.value,
      minInProgressValue: 2,
      maxInProgressValue: 99.4
    };
  },
  computed: {
    isPercentWidth() {
      return isPercent(this.width);
    },
    styleObj() {
      if (isNumber(this.width)) {
        return {
          width: `${this.width}px`
        };
      }
      return {
        width: this.width
      };
    },
    relativeStrokeWidth() {
      const {
        strokeWidth,
        computedWidth
      } = this;
      return getRelativeStrokeWidth({
        strokeWidth,
        width: computedWidth
      });
    },
    relativeHeight() {
      const {
        height,
        computedWidth
      } = this;
      return getRelativeThickness({
        thickness: height,
        width: computedWidth
      });
    },
    isInProgress() {
      return isInProgress(this.internalValue);
    },
    isComplete() {
      return isComplete(this.internalValue);
    },
    isNotStarted() {
      return isNotStarted(this.internalValue);
    },
    progressWidth() {
      const strokeWidth = this.relativeStrokeWidth * 2;
      const progress = this.internalValue - strokeWidth;
      return clamp(progress, 0, 100);
    },
    svgRadius() {
      return this.radius >= 0 ? this.radius : this.relativeHeight / 2;
    }
  },
  watch: {
    value() {
      if (!this.trickle) {
        this.internalValue = this.value;
        return;
      }
      const value = clamp(this.value, this.internalValue, 100);
      if (value === 100) {
        this.done();
      } else {
        this.increment(value);
      }
    }
  },
  mounted: _async(function () {
    const _this = this;
    return _await(_this.$nextTick(), function () {
      if (_this.isPercentWidth) {
        _this.setComputedWidth({
          width: _this.$el.offsetWidth
        });
      }
    });
  }),
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    setComputedWidth(_ref) {
      let {
        width
      } = _ref;
      requestAnimationFrame(() => {
        this.computedWidth = width;
      });
    },
    increment(amount) {
      if (this.isNotStarted) {
        this.start();
      }
      if (this.isComplete) {
        return;
      }
      if (typeof amount !== 'number') {
        amount = this.internalValue + getTrickleProgress(this.internalValue);
      }
      amount = clamp(amount, 0, this.maxInProgressValue);
      this.set(amount);
    },
    done(force) {
      if (!force && !this.internalValue) {
        return;
      }
      this.increment(30 + 50 * Math.random());
      this.set(100);
    },
    start() {
      if (this.isNotStarted) {
        this.set(0);
      }
      if (this.trickle) {
        this.timer = setInterval(() => {
          this.increment();
          if (this.isComplete) {
            clearInterval(this.timer);
          }
        }, this.trickleSpeed);
      }
    },
    set(value) {
      value = clamp(value, this.minInProgressValue, 100);
      queue(next => {
        if (value === 1) {
          setTimeout(() => {
            setTimeout(() => {
              this.internalValue = 100;
              next();
            }, this.speed);
          }, this.speed);
        } else {
          this.internalValue = value;
          setTimeout(next, this.speed);
        }
      });
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-progress-bar.vue2.js.map
